import { useState, useEffect } from 'react';
import { collection, query, where, orderBy, limit, getDocs, doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';

interface SnapshotData {
  date: Date;
  price: number;
  volume: number;
}

interface UseStockHistoryResult {
  data: SnapshotData[];
  loading: boolean;
  error: string | null;
  pivotPrice?: number;
  currentPrice?: number;
  priceChgPercent?: number;
  lastUpdated?: Date;
  dataDelay?: number;
  currentVolume?: number;
  volumeChgVs50Day?: number;
}

export const useStockHistory = (symbol: string, days: number = 30): UseStockHistoryResult => {
  const [data, setData] = useState<SnapshotData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pivotPrice, setPivotPrice] = useState<number | undefined>(undefined);
  const [currentPrice, setCurrentPrice] = useState<number | undefined>(undefined);
  const [priceChgPercent, setPriceChgPercent] = useState<number | undefined>(undefined);
  const [lastUpdated, setLastUpdated] = useState<Date | undefined>(undefined);
  const [dataDelay, setDataDelay] = useState<number | undefined>(undefined);
  const [currentVolume, setCurrentVolume] = useState<number | undefined>(undefined);
  const [volumeChgVs50Day, setVolumeChgVs50Day] = useState<number | undefined>(undefined);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user || !symbol) {
      setLoading(false);
      return;
    }

    const fetchHistory = async () => {
      try {
        setLoading(true);
        setError(null);

        // First, get the pivot price and other data from watchlist
        const watchlistDocId = `${user.uid}_${symbol}`;
        const watchlistDoc = await getDoc(doc(db, 'watchlist', watchlistDocId));
        let currentPivotPrice: number | undefined;
        let latestCurrentPrice: number | undefined;
        let latestPriceChgPercent: number | undefined;
        let latestLastUpdated: Date | undefined;
        let latestDataDelay: number | undefined;
        let latestVolumeChg: number | undefined;
        let addedToWatchlistAt: Date | undefined;

        if (watchlistDoc.exists()) {
          const watchlistData = watchlistDoc.data();
          if (watchlistData.pivotPrice && watchlistData.pivotPrice > 0) {
            currentPivotPrice = watchlistData.pivotPrice;
          }
          if (watchlistData.currentPrice !== undefined) {
            latestCurrentPrice = watchlistData.currentPrice;
          }
          if (watchlistData.priceChgPercent !== undefined) {
            latestPriceChgPercent = watchlistData.priceChgPercent;
          }
          if (watchlistData.lastUpdated) {
            latestLastUpdated = watchlistData.lastUpdated.toDate ? watchlistData.lastUpdated.toDate() : new Date(watchlistData.lastUpdated);
          }
          if (watchlistData.dataDelay !== undefined) {
            latestDataDelay = watchlistData.dataDelay;
          }
          if (watchlistData.volumeChgVs50Day !== undefined) {
            latestVolumeChg = watchlistData.volumeChgVs50Day;
          }
          // Get when user added this stock to their watchlist
          if (watchlistData.addedAt) {
            addedToWatchlistAt = watchlistData.addedAt.toDate ? watchlistData.addedAt.toDate() : new Date(watchlistData.addedAt);
          }
        }

        // Get current volume from stocks collection
        let latestVolume: number | undefined;
        const stockDoc = await getDoc(doc(db, 'stocks', symbol));

        if (stockDoc.exists()) {
          const stockData = stockDoc.data();
          if (stockData.volume) {
            latestVolume = stockData.volume;
          }
        }

        // Calculate the date range
        // Start from when user added to watchlist, or N days ago, whichever is more recent
        const endDate = new Date();
        const daysAgoDate = new Date();
        daysAgoDate.setDate(daysAgoDate.getDate() - days);

        // Use the later of: addedToWatchlist date or daysAgo date
        // This ensures new users only see data from when they started tracking
        const startDate = addedToWatchlistAt && addedToWatchlistAt > daysAgoDate
          ? addedToWatchlistAt
          : daysAgoDate;

        // Query ALL snapshots for this symbol (not filtered by userId)
        // Prefer Alpaca data for charts as it's more reliable for price/volume history
        const snapshotsQuery = query(
          collection(db, 'watchlistSnapshots'),
          where('symbol', '==', symbol),
          where('timestamp', '>=', startDate),
          where('timestamp', '<=', endDate),
          orderBy('timestamp', 'desc'),
          limit(days * 4) // Get more data points since we're querying all sources
        );

        const querySnapshot = await getDocs(snapshotsQuery);

        // Group snapshots by LOCAL date (not UTC), keeping the best data for each day
        // Prefer Alpaca data over MarketSurge for price/volume charting
        const snapshotsByDate = new Map<string, { date: Date; price: number; volume: number; source: string }>();

        querySnapshot.forEach((doc) => {
          const data = doc.data();
          const date = data.timestamp.toDate();
          // Use LOCAL date for grouping to avoid timezone issues
          const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

          const source = data.source || data.dataProvider || 'unknown';

          // Skip MarketSurge data for charts - Alpaca is more reliable for price/volume history
          if (source === 'marketsurge') {
            return;
          }

          const existing = snapshotsByDate.get(dateKey);
          const newVolume = data.volume || 0;
          // Alpaca saves as 'price', MarketSurge/manual saves as 'currentPrice'
          const newPrice = data.price || data.currentPrice || 0;

          // Keep this snapshot if:
          // 1. We don't have data for this date yet, OR
          // 2. This is Alpaca data and existing is not, OR
          // 3. This snapshot has volume and existing doesn't, OR
          // 4. This snapshot is more recent on the same day with valid data
          const isAlpaca = source === 'alpaca';
          const existingIsAlpaca = existing?.source === 'alpaca';

          if (!existing ||
              (isAlpaca && !existingIsAlpaca) ||
              (newVolume > 0 && existing.volume === 0) ||
              (newVolume > 0 && newPrice > 0 && date > existing.date)) {
            snapshotsByDate.set(dateKey, {
              date,
              price: newPrice || existing?.price || 0,
              volume: newVolume || existing?.volume || 0,
              source
            });
          }
        });

        // Convert map to array (without source field for the chart data)
        const snapshots: SnapshotData[] = Array.from(snapshotsByDate.values()).map(s => ({
          date: s.date,
          price: s.price,
          volume: s.volume
        }));

        // Sort by date ascending for chart display
        snapshots.sort((a, b) => a.date.getTime() - b.date.getTime());

        setData(snapshots);
        setPivotPrice(currentPivotPrice);
        setCurrentPrice(latestCurrentPrice);
        setPriceChgPercent(latestPriceChgPercent);
        setLastUpdated(latestLastUpdated);
        setDataDelay(latestDataDelay);
        setCurrentVolume(latestVolume);
        setVolumeChgVs50Day(latestVolumeChg);
      } catch (err) {
        console.error('Error fetching stock history:', err);
        setError('Failed to load historical data');
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [symbol, days]);

  return { data, loading, error, pivotPrice, currentPrice, priceChgPercent, lastUpdated, dataDelay, currentVolume, volumeChgVs50Day };
};